import { IChatV2Schema } from '@cian/chats-api';
import { NotFoundError } from '@cian/peperrors';

import {
  fetchChatsMarkAsStarred,
  TChatsMarkAsStarredRequest,
  TChatsMarkAsStarredResponse,
} from '../../repositories/chats/v1/chats/mark-as-starred';
import { showAlert } from '../../slices/alertSlice';
import { EAlert } from '../../types/alert';
import { EExceptionDomain } from '../../types/exceptionDomains';
import { TThunkAction } from '../../types/redux/thunk';
import { ETelemetryEvents } from '../../types/telemetry';
import { getGenericLikeError } from '../../utils/logging/getGenericLikeError';
import { getResponseError } from '../../utils/logging/getResponseError';
import { deselectAllChats, toggleChatListPending, updatedFromServer } from '../chatList/actions';

const DEFAULT_ERROR_MESSAGE = 'Не удалось добавить чат в важные';

/**
 * Пометить чат как важный
 * @param parameters.chatId id чата
 */
export const markChatAsStarred =
  (parameters: TChatsMarkAsStarredRequest): TThunkAction<Promise<void>> =>
  async (dispatch, getState, context) => {
    const { httpApi, logger, telemetry } = context;

    dispatch(toggleChatListPending(true));

    try {
      const { statusCode, response }: TChatsMarkAsStarredResponse = await fetchChatsMarkAsStarred({
        httpApi,
        config: { requestConfig: { withCredentials: true } },
        parameters: { chatId: parameters.chatId },
      });

      if (statusCode !== 200) {
        throw getResponseError({
          domain: EExceptionDomain.Chat,
          response,
          defaultMessage: DEFAULT_ERROR_MESSAGE,
          statusCode,
        });
      }

      if (!response.chat) {
        throw new NotFoundError({
          domain: EExceptionDomain.Chat,
          message: 'Чат для добавления в важные не найден',
          details: {
            chatId: parameters.chatId,
          },
        });
      }

      const chat = response.chat as unknown as IChatV2Schema;

      dispatch(updatedFromServer(chat));
      telemetry.increment(ETelemetryEvents.MarkMessagesAsStarredSuccess);
      dispatch(deselectAllChats());

      /**
       * При успешном помечании чата как важного, показываем алерт
       */
      dispatch(showAlert({ type: EAlert.Info, message: 'Чат добавлен в важные' }));
    } catch (ex) {
      telemetry.increment(ETelemetryEvents.MarkMessagesAsStarredFail);

      const exception = getGenericLikeError({
        error: ex,
        domain: EExceptionDomain.Chat,
        message: DEFAULT_ERROR_MESSAGE,
      });

      logger.error(exception, {
        microserviceName: 'chats',
        pathApi: '/v1/chats/mark-as-starred/',
        parameters: JSON.stringify({ chatId: parameters.chatId }),
      });

      dispatch(showAlert({ message: DEFAULT_ERROR_MESSAGE }));
    } finally {
      dispatch(toggleChatListPending(false));
    }
  };
